// Loads the next images of product cards, product lists, the product page gallery and Carousel sections before they show.

const SLIDESHOWS = [
  'product-card slideshow-component',
  'slideshow-component.resource-list__carousel',
  'media-gallery slideshow-component',
  '[data-testid="carousel-section"] slideshow-component',
].join(', ');

const observed = new Set();
const nearby = new Set();

function load(slide) {
  const owner = slide?.closest('slideshow-component');
  slide?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    // In a product list slide, only the card's showing image; its others load on hover.
    const inner = img.closest('slideshow-component');
    if (inner !== owner && inner?.slides?.[inner.current] !== img.closest('slideshow-slide')) return;
    // Without lazy, Chrome reads sizes starting with auto as the whole window width.
    img.sizes = img.sizes.replace(/^\s*auto\s*,\s*/, '');
    img.removeAttribute('loading');
  });
}

const canHover = matchMedia('(hover: hover) and (pointer: fine)');

function loadAround(slideshow, index, both = false) {
  // A card inside another carousel can't be swiped; its next image shows only on mouse hover.
  if (slideshow.isNested && !canHover.matches) return;
  const slides = slideshow.slides;
  if (!slides?.length) return;
  const count = slides.length;
  // Arrows move a whole page of slides, so load the page showing and the next one, plus in a
  // product list or Carousel section the slide that peeks in after it (not counted as visible).
  const page = Math.max(1, slideshow.visibleSlides?.length ?? 1);
  const peek = slideshow.closest('product-card, media-gallery') ? 0 : 1;
  const at = (i) => (slideshow.infinite ? slides[(i + count) % count] : slides[i]);
  for (let i = 0; i < page * 2 + peek && i < count; i++) load(at(index + i));
  if (both) for (let i = 1; i <= page && i < count; i++) load(at(index - i));
}

const intersection = new IntersectionObserver(
  (entries) => {
    for (const { target, isIntersecting } of entries) {
      if (!isIntersecting) {
        nearby.delete(target);
        continue;
      }
      nearby.add(target);
      loadAround(target, target.current ?? 0);
    }
  },
  { rootMargin: '400px 0px' }
);

function updateObserved() {
  for (const slideshow of observed) {
    if (slideshow.isConnected) continue;
    observed.delete(slideshow);
    nearby.delete(slideshow);
    intersection.unobserve(slideshow);
  }
  document.querySelectorAll(SLIDESHOWS).forEach((slideshow) => {
    if (observed.has(slideshow)) return;
    observed.add(slideshow);
    intersection.observe(slideshow);
  });
}

function start() {
  document.addEventListener('slideshow:select', (event) => {
    const slideshow = event.target;
    if (!(slideshow instanceof Element) || !slideshow.matches(SLIDESHOWS)) return;
    loadAround(slideshow, event.detail.index, event.detail.userInitiated);
  });

  // A morph can swap a nearby card's images without it entering the screen.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      updateObserved();
      for (const slideshow of nearby) loadAround(slideshow, slideshow.current ?? 0);
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['loading'] });

  customElements.whenDefined('slideshow-component').then(updateObserved);
}

if (document.readyState === 'complete') start();
else window.addEventListener('load', start, { once: true });
